import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { MessageCircle, Loader2 } from "lucide-react";
import { API, waLink, renderLinkedText } from "@/lib/content";
import { useContent } from "@/context/ContentContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";

export default function DynamicPage() {
  const { slug } = useParams();
  const { content } = useContent();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPage(null);
    setNotFound(false);
    axios
      .get(`${API}/pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#FAF8F5] px-6 text-center" data-testid="page-not-found">
        <p className="font-display text-4xl font-medium text-[#1C1917]">Page not found</p>
        <p className="mt-3 text-sm text-[#57534E]">This page may have been removed or is not published yet.</p>
        <Link to="/" className="mt-8 rounded-full bg-[#7A2B21] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#5C1E16]">
          Back to Home
        </Link>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="animate-spin text-[#7A2B21]" size={32} />
      </div>
    );
  }

  return (
    <div className="grain bg-[#FAF8F5]" data-testid="dynamic-page">
      <title>{page.seo_title || `${page.title} | Tricity Family Concierge`}</title>
      {page.seo_description ? <meta name="description" content={page.seo_description} /> : null}
      {page.noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      <Navbar />
      <main>
        <section className="bg-[#1C1917] pb-20 pt-40 lg:pt-48">
          <div className="mx-auto max-w-5xl px-6 lg:px-10">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#FAF8F5] sm:text-5xl"
              data-testid="dynamic-page-title"
            >
              {page.title}
            </motion.h1>
            {page.subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
                className="mt-6 max-w-2xl text-base leading-relaxed text-[#FAF8F5]/80 md:text-lg"
                data-testid="dynamic-page-subtitle"
              >
                {page.subtitle}
              </motion.p>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-20 lg:px-10">
          <div className="space-y-14">
            {(page.blocks || []).map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="border-l-2 border-[#C5A059] pl-8"
                data-testid={`dynamic-block-${i}`}
              >
                {b.heading && (
                  <h2 className="font-display text-2xl font-medium tracking-tight text-[#1C1917] md:text-3xl">
                    {b.heading}
                  </h2>
                )}
                {b.text && (
                  <p className="mt-3 max-w-2xl whitespace-pre-line text-base leading-relaxed text-[#57534E]">
                    {renderLinkedText(b.text)}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        <section className="bg-[#7A2B21] py-20">
          <div className="mx-auto flex max-w-5xl flex-col items-start gap-8 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-10">
            <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-[#FAF8F5]">
              Questions? <span className="italic text-[#C5A059]">We're one message away.</span>
            </h2>
            <a
              href={waLink(content.business.whatsapp_number, `Hi! I just read "${page.title}" on your website and would like to know more.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dynamic-page-whatsapp"
              className="inline-flex shrink-0 items-center gap-3 rounded-full bg-[#FAF8F5] px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#7A2B21] transition-[background-color,transform] duration-300 hover:bg-[#C5A059] hover:text-[#1C1917] hover:scale-[1.03]"
            >
              <MessageCircle size={17} />
              WhatsApp Us
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
